import { Show } from "solid-js";
import {
  GOVERN_GUIDANCE_KEY,
  GOVERN_SELF_HANDLED_GUIDANCE_KEYS,
  GOVERNANCE_STATE,
} from "../../enums/govern";
import type { DeliveryBrief } from "../brief/briefTypes";
import { governanceNextAction } from "./governGuidance";
import type { GovernEditor, GovernWorkItem } from "./governTypes";

export function GovernActionBar(props: {
  work: GovernWorkItem;
  brief: DeliveryBrief | null;
  canVerify: boolean;
  sourceReady: boolean;
  onSetState: (state: GovernWorkItem["state"]) => void;
  onOpenEditor: (editor: GovernEditor, trigger: HTMLElement) => void;
  onVerify: () => void;
}) {
  const nextAction = () => governanceNextAction(props.work, props.brief);
  return (
    <section class="govern-action-bar" aria-label="Governance action">
      <div>
        <span>Next required action</span>
        <strong role="status" aria-live="polite">
          {nextAction().message}
        </strong>
      </div>
      <Show
        when={
          props.sourceReady &&
          props.work.state === GOVERNANCE_STATE.READY_FOR_DELIVERY
        }
      >
        <button
          type="button"
          class="govern-primary-action"
          disabled={!props.sourceReady}
          onClick={() => props.onSetState(GOVERNANCE_STATE.IN_DELIVERY)}
        >
          Start delivery
        </button>
      </Show>
      <Show
        when={
          props.sourceReady && props.work.state === GOVERNANCE_STATE.IN_DELIVERY
        }
      >
        <button
          type="button"
          class="govern-primary-action"
          onClick={() => props.onSetState(GOVERNANCE_STATE.READY_FOR_REVIEW)}
        >
          Mark ready for review
        </button>
      </Show>
      <Show
        when={
          props.sourceReady &&
          props.work.state === GOVERNANCE_STATE.READY_FOR_REVIEW
        }
      >
        <Show when={nextAction().key === GOVERN_GUIDANCE_KEY.ASSIGN_ROLES}>
          <button
            type="button"
            class="govern-primary-action"
            onClick={(event) =>
              props.onOpenEditor({ kind: "roles" }, event.currentTarget)
            }
          >
            Assign roles
          </button>
        </Show>
        <Show
          when={nextAction().key === GOVERN_GUIDANCE_KEY.SEND_BACK_FOR_REWORK}
        >
          <button
            type="button"
            class="govern-primary-action"
            onClick={(event) =>
              props.onOpenEditor({ kind: "rework" }, event.currentTarget)
            }
          >
            Send back for rework
          </button>
        </Show>
        <Show
          when={!GOVERN_SELF_HANDLED_GUIDANCE_KEYS.includes(nextAction().key)}
        >
          <div class="govern-action-buttons">
            <button
              type="button"
              class="govern-primary-action"
              disabled={!props.canVerify}
              onClick={props.onVerify}
            >
              Verify complete
            </button>
            <button
              type="button"
              onClick={(event) =>
                props.onOpenEditor({ kind: "rework" }, event.currentTarget)
              }
            >
              Send back for rework
            </button>
          </div>
        </Show>
      </Show>
      <Show
        when={
          props.sourceReady &&
          props.work.state === GOVERNANCE_STATE.NEEDS_REWORK
        }
      >
        <button
          type="button"
          class="govern-primary-action"
          onClick={() => props.onSetState(GOVERNANCE_STATE.IN_DELIVERY)}
        >
          Resume delivery
        </button>
      </Show>
      <Show
        when={
          props.sourceReady && props.work.state === GOVERNANCE_STATE.VERIFIED
        }
      >
        <button
          type="button"
          class="govern-primary-action"
          onClick={(event) =>
            props.onOpenEditor({ kind: "schedule" }, event.currentTarget)
          }
        >
          Schedule next review
        </button>
      </Show>
      <Show
        when={
          props.sourceReady &&
          props.work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED
        }
      >
        <div class="govern-action-buttons">
          <button
            type="button"
            class="govern-primary-action"
            onClick={(event) =>
              props.onOpenEditor({ kind: "schedule" }, event.currentTarget)
            }
          >
            Edit review schedule
          </button>
          <button
            type="button"
            onClick={() => props.onSetState(GOVERNANCE_STATE.READY_FOR_REVIEW)}
          >
            Reopen for review
          </button>
        </div>
      </Show>
    </section>
  );
}
